import { createJSONStorage, StateStorage } from 'zustand/middleware'

export const safeLocalStorage: StateStorage = {
    getItem: (key: string): string | null => {
        try {
            if (typeof window === 'undefined') return null

            return localStorage.getItem(key)
        } catch (error) {
            console.error(`Error reading localStorage key "${key}":`, error)
            return null
        }
    },
    setItem: (key: string, value: string): void => {
        try {
            if (typeof window === 'undefined') return

            localStorage.setItem(key, value)
        } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error)
            // Try to clear the key if it's corrupted
            try {
                localStorage.removeItem(key)
            } catch (removeError) {
                console.error(`Error removing corrupted localStorage key "${key}":`, removeError)
            }
        }
    },
    removeItem: (key: string): void => {
        try {
            if (typeof window === 'undefined') return

            localStorage.removeItem(key)
        } catch (error) {
            console.error(`Error removing localStorage key "${key}":`, error)
        }
    },
}

/**
 * Creates a safe JSON storage for Zustand with error handling
 */
export const createSafeJSONStorage = () => createJSONStorage(() => safeLocalStorage)

/**
 * Safely rehydrates storage. If there is an error, it will clear the corrupted data.
 */
export const createSafeLocaleStorageRehydration = <T>(storageKey: string) => {
    return () => (_: T, error: any) => {
        if (error) {
            console.error(`Failed to rehydrate storage for key "${storageKey}":`, error)

            // Clear corrupted data
            safeLocalStorage.removeItem(storageKey)
        }
    }
}

/**
 * A function which can be passed as a migrate function to a zustand persist function
 * It will return null if the version is not the same as the version from which to delete the storage
 *
 * @param versionToCompare The version from which to delete the storage
 */
export const safeDeleteStorageOnVersionMismatch = <T>(versionToCompare: number) => {
    return () => (_: T, version: number) => {
        if (version !== versionToCompare) {
            return null
        }
    }
}
